import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Search,
  ShoppingCart,
  Euro,
  Calendar,
  Building2,
  FolderOpen,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Truck,
  Package,
  Filter,
  Play,
} from 'lucide-react';
import { PurchaseOrderFormDialog } from '@/components/PurchaseOrders/PurchaseOrderFormDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PurchaseOrder {
  id: string;
  po_number: number;
  customer_po_reference: string;
  description: string;
  customer_id: string;
  project_id: string;
  budget_id: string;
  status: string;
  total_amount: number;
  order_date: string;
  delivery_date: string;
  customer?: { company_name: string; customer_number: number };
  project?: { name: string; project_number: number };
  budget?: { budget_number: number; title: string };
}

export default function PurchaseOrders() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);

  const fetchOrders = async () => {
    try {
      const { data, error } = await api
        .from('purchase_orders')
        .select('*')
        .eq('is_active', true)
        .order('po_number', { ascending: false });

      if (error) throw error;

      // Fetch related data
      const customerIds = [...new Set((data || []).map((o: any) => o.customer_id).filter(Boolean))];
      const projectIds = [...new Set((data || []).map((o: any) => o.project_id).filter(Boolean))];
      const budgetIds = [...new Set((data || []).map((o: any) => o.budget_id).filter(Boolean))];

      const [customersRes, projectsRes, budgetsRes] = await Promise.all([
        customerIds.length > 0
          ? api.from('customers').select('id,company_name,customer_number').in('id', customerIds)
          : { data: [] },
        projectIds.length > 0
          ? api.from('projects').select('id,name,project_number').in('id', projectIds)
          : { data: [] },
        budgetIds.length > 0
          ? api.from('budgets').select('id,budget_number,title').in('id', budgetIds)
          : { data: [] },
      ]);

      const customersMap = Object.fromEntries((customersRes.data || []).map((c: any) => [c.id, c]));
      const projectsMap = Object.fromEntries((projectsRes.data || []).map((p: any) => [p.id, p]));
      const budgetsMap = Object.fromEntries((budgetsRes.data || []).map((b: any) => [b.id, b]));

      const enrichedData = (data || []).map((o: any) => ({
        ...o,
        customer: customersMap[o.customer_id],
        project: projectsMap[o.project_id],
        budget: budgetsMap[o.budget_id],
      }));

      setOrders(enrichedData);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los pedidos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.customer_po_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(order.po_number).includes(searchTerm);

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleEdit = (order: PurchaseOrder) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedOrder(null);
    setDialogOpen(true);
  };

  const getStatusConfig = (status: string) => {
    const config: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: any; color: string }> = {
      pending: { label: 'Pendiente', variant: 'secondary', icon: Clock, color: 'text-amber-500' },
      confirmed: { label: 'Confirmado', variant: 'default', icon: CheckCircle, color: 'text-blue-500' },
      in_production: { label: 'En Producción', variant: 'default', icon: Play, color: 'text-purple-500' },
      shipped: { label: 'Enviado', variant: 'outline', icon: Truck, color: 'text-cyan-500' },
      delivered: { label: 'Entregado', variant: 'outline', icon: Package, color: 'text-green-500' },
      cancelled: { label: 'Cancelado', variant: 'destructive', icon: XCircle, color: 'text-red-500' },
    };
    return config[status] || config.pending;
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    inProduction: orders.filter(o => o.status === 'in_production').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    totalAmount: orders.reduce((sum, o) => sum + (o.total_amount || 0), 0),
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Pedidos (PO)
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestión de pedidos de clientes
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Pedido
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {stats.total}
                </p>
              </div>
              <ShoppingCart className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pendientes</p>
                <p className="text-3xl font-bold text-amber-500">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">En Producción</p>
                <p className="text-3xl font-bold text-purple-500">{stats.inProduction}</p>
              </div>
              <Play className="w-8 h-8 text-purple-500/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Entregados</p>
                <p className="text-3xl font-bold text-green-500">{stats.delivered}</p>
              </div>
              <Package className="w-8 h-8 text-green-500/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Importe Total</p>
                <p className="text-2xl font-bold text-primary">
                  {stats.totalAmount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </p>
              </div>
              <Euro className="w-8 h-8 text-primary/50" />
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar pedidos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
              <SelectItem value="confirmed">Confirmado</SelectItem>
              <SelectItem value="in_production">En Producción</SelectItem>
              <SelectItem value="shipped">Enviado</SelectItem>
              <SelectItem value="delivered">Entregado</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <ShoppingCart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm || statusFilter !== 'all' ? 'No se encontraron pedidos' : 'No hay pedidos registrados'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOrders.map((order) => {
              const statusConfig = getStatusConfig(order.status);
              const StatusIcon = statusConfig.icon;

              return (
                <Card key={order.id} className="glass-card p-6 hover:shadow-magma transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="font-mono bg-primary/10">
                          PO-{String(order.po_number).padStart(4, '0')}
                        </Badge>
                        <Badge variant={statusConfig.variant} className="gap-1">
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      {order.customer_po_reference && (
                        <p className="text-sm text-muted-foreground">
                          Ref. Cliente: <span className="font-medium">{order.customer_po_reference}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {order.customer && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Building2 className="w-4 h-4" />
                        <span className="truncate">
                          C-{String(order.customer.customer_number).padStart(3, '0')} - {order.customer.company_name}
                        </span>
                      </div>
                    )}

                    {order.project && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <FolderOpen className="w-4 h-4" />
                        <span className="truncate">
                          P-{String(order.project.project_number).padStart(4, '0')} - {order.project.name}
                        </span>
                      </div>
                    )}

                    {order.budget && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <FileText className="w-4 h-4" />
                        <span className="truncate">
                          PRE-{String(order.budget.budget_number).padStart(4, '0')}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {order.order_date ? format(new Date(order.order_date), 'dd MMM yyyy', { locale: es }) : '-'}
                      </span>
                      {order.delivery_date && (
                        <span className="text-xs">
                          → {format(new Date(order.delivery_date), 'dd MMM', { locale: es })}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Euro className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-lg text-primary">
                        {(order.total_amount || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      </span>
                    </div>
                  </div>

                  {canManage && (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => handleEdit(order)}
                      >
                        Editar
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <PurchaseOrderFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        order={selectedOrder}
        onSuccess={fetchOrders}
      />
    </DashboardLayout>
  );
}
